/*
Licensed Materials - Property of IBM
694906H
(c) Copyright IBM Corp.  2020 All Rights Reserved

US Government Users Restricted Rights - Use, duplication or disclosure restricted
by GSA ADP Schedule Contract with IBM Corp.
*/

import { gql, useMutation } from '@apollo/client';
import pick from 'just-pick';
import { Address, LegalAddressTocart } from '../model/types';

/* prettier-ignore */
const cleanAddress = (addr: Address) => {
  return pick(addr,
    [
      'address1',
      'address2',
      'city',
      'company',
      'firstName',
      'lastName',
      'name',
      'phone',
      'province',
      'zip',
      'email',
      'titleCode',
      'dateOfBirth',
      'identification',
      'countryCode',
      'orderId',
      'addressType'
    ]);
};

const ADD_ADDRESS = gql`
  mutation AddAddress($address: CusAddressInput!) {
    customerAddAddress(address: $address)
  }
`;

const  ASSOCIATE_LEGAL_ADDRESS_TO_CART = gql`
mutation AssociateLegalAddressToCart($input: LegalAddressToCartInput!) {
  associateLegalAddressToCart(input: $input) {
    success
  }
}
`;

const UPDATE_ADDRESS = gql`
  mutation UpdateAddress($id: ID!, $address: CusAddressInput!) {
    customerUpdateAddress(id: $id, address: $address)
  }
`;

const DELETE_ADDRESS = gql`
  mutation DeleteAddress($id: ID) {
    customerDeleteAddress(id: $id)
  }
`;

const UPDATE_DEFAULT_ADDRESS = gql`
  mutation UpdateDefaultAddress($address: CusAddressInput!) {
    customerUpdateDefaultAddress(address: $address)
  }
`;

const SET_PASSWORD = requireOldPassword =>
  requireOldPassword
    ? gql`
        mutation SetPassword($password: String, $oldPassword: String) {
          customerSetPassword(password: $password, currentPassword: $oldPassword)
        }
      `
    : gql`
        mutation SetPassword($password: String) {
          customerSetPassword(password: $password)
        }
      `;

const REGISTER = gql`
  mutation Register($user: CusCustomerInput!, $address: CusAddressInput!) {
    customerRegister(user: $user, address: $address)
  }
`;

const SUBMIT_RESET_PASSWORD_STEP_ONE = gql`
    mutation Mutation($username: String!) {
        customerForgetPasswordStepOne(username: $username)
    }
`;

const SUBMIT_RESET_PASSWORD_STEP_TWO = gql`
  mutation CustomerForgetPasswordStepTwo($username: String!, $password: String!, $passwordVerify: String!, $validationCode: String!) {
    customerForgetPasswordStepTwo(username: $username, password: $password, passwordVerify: $passwordVerify, validationCode: $validationCode)
  }
`;

const SUBMIT_CHANGE_PASSWORD = gql`
  mutation Mutation($username: String!, $password: String!, $passwordNew: String!, $passwordVerify: String!) {
  authPasswordReset(username: $username, password: $password, passwordNew: $passwordNew, passwordVerify: $passwordVerify) {
    errorMessage
    expiresAt
    token
    wasSuccessful
  }
}
`;

  function xorCipher(text) {
    let result = '';
    const key = `AWgOUVASBkKILULsnrJu3bcVK/HTxLUXCdxfTRMt5OzO7P6bgjGrTAvfNveCFvTz
                KkMJigNbTyxFayVx5/PU91Gl91rXDJF0px/MZzT+KjH4t9cNvhlI+cmOwwduop+h
                bB7+oD1uUAUChqE2Ppnr94JZozKRs3Mn97o8xBItQfM=`
    for (let i = 0; i < text.length; i++) {
        result += String.fromCharCode(text.charCodeAt(i) ^ key.charCodeAt(i % key.length));
    }
    return result;
  } 
export const useProfileModification = (args?: Args) => {
  const [addressAdd] = useMutation(ADD_ADDRESS);
  const [addressUpdate] = useMutation(UPDATE_ADDRESS);
  const [addressDelete] = useMutation(DELETE_ADDRESS);
  const [setPassword] = useMutation(SET_PASSWORD(args?.requireOldPassword));
  const [register] = useMutation(REGISTER);
  const [updateDefaultAddress] = useMutation(UPDATE_DEFAULT_ADDRESS);
  const [resetpasswordOne] = useMutation(SUBMIT_RESET_PASSWORD_STEP_ONE);
  const [resetpasswordTwo] = useMutation(SUBMIT_RESET_PASSWORD_STEP_TWO);
  const [legalAddressToCart] = useMutation(ASSOCIATE_LEGAL_ADDRESS_TO_CART);
  const [changepassword] = useMutation(SUBMIT_CHANGE_PASSWORD);

  return {
    addAddress: async (address: Address) => {
      return addressAdd({ variables: { address } });
    },

    legalAddressToCart: async(input: LegalAddressTocart ) => {
      return legalAddressToCart({variables: { input }})
    },

    updateAddress: async (id: string, address: Address) => {
      return addressUpdate({ variables: { id, address: cleanAddress(address) } });
    },

    deleteAddress: async (id: string) => {
      return addressDelete({ variables: { id } });
    },

    setPassword: async (password: string, oldPassword?: string) => {
      return setPassword({ variables: { password, ...(oldPassword ? { oldPassword } : {}) } });
    },
    
    register: async (user: Address & { password: string }) => {
      return register({
        variables: {
          user: { username: user.email, password: xorCipher(user.password) },
          address: {
            address1: user.address1,
            city: user.city,
            country: user.countryCode,
            firstName: user.firstName,
            lastName: user.lastName,
            name: `${user.lastName}, ${user.firstName}`,
            phone: user.phone,
            province: user.province,
            zip: user.zip,
            email: user.email,
            personTitle: user.titleCode,
            phoneExt: user.extension
          }
        }
      });
    },

    updateDefaultAddress: async (address: Address) => {
      return updateDefaultAddress({ variables: { address } });
    },

    resetPasswordStepOne: async (username: string) => {
      return resetpasswordOne({ variables: { username } });
    },

    resetPasswordStepTwo: async (username: string, validationCode: string, password: string, passwordVerify: string) => {
      return resetpasswordTwo({ variables: { username, validationCode, password, passwordVerify } });
    },

    changePasswordExpire: async (username: string, password: string, passwordNew: string, passwordVerify: string) => {
      return changepassword({ variables: { username, password, passwordNew, passwordVerify } });
    }

  };
};

type Args = {
  requireOldPassword?: boolean;
};
